import { useState } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { STAGE_LABELS, toGrade, type AssessmentStage } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  SearchForm,
  Skeleton,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { LostArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { assessmentStatusVariant, PILL_CLASS, slaVariant } from '@/lib/badges';
import { pendingAtStage, usePendingAssessments, type Assessment } from '@/lib/assessments-api';
import { cn } from '@/lib/utils';

function dueLabel(dueAt: string | null, overdue: boolean): { text: string; state: 'overdue' | 'due-soon' | 'on-track' } {
  if (!dueAt) return { text: 'No deadline', state: 'on-track' };
  const ms = new Date(dueAt).getTime() - Date.now();
  const days = Math.ceil(ms / 86_400_000);
  if (overdue || days < 0) return { text: `${Math.abs(days)}d overdue`, state: 'overdue' };
  if (days <= 1) return { text: days === 0 ? 'Due today' : 'Due tomorrow', state: 'due-soon' };
  return { text: `${days}d left`, state: 'on-track' };
}

/**
 * The rating this row already carries — the last stage that recorded a number.
 *
 * `stages` is ordered by submittedAt, so the last non-null is what the reviewer is
 * being asked to agree or disagree with: the self rating in a manager's queue, the
 * manager's rating in HR's. Named by its stage rather than shown bare, because
 * "7.5" with no author is not a fact anybody can act on.
 */
function recordedRating(a: Assessment) {
  return [...a.stages].reverse().find((s) => s.overallRating !== null) ?? null;
}

/**
 * One stage of the review chain: what is waiting on you *there*.
 *
 * Stage-scoped, not "everything waiting on you across every stage". It used to be the
 * latter while the sidebar counted per stage, so "HR reviews · 3" opened a list of
 * eight - the pill promised a filter the page did not apply, and the same eight rows
 * appeared under two different names for anyone holding both stages. The count and the
 * rows come from one `pendingAtStage` call now, so they cannot disagree.
 *
 * Both entries are kept rather than merged into one: someone who is HR *and* has direct
 * reports genuinely owns two queues - their own reports at the manager stage, and
 * everybody at the HR stage - and one merged list would hide which hat a row needs.
 *
 * Sorted by the server on managerDueAt, so the 3-day SLA drives the order rather
 * than alphabetical name - the point of the queue is what to do next, not who.
 */
export function ReviewsPage({ stage }: { stage: AssessmentStage }) {
  const { data: all, isPending, error } = usePendingAssessments();
  const [query, setQuery] = useState('');

  if (isPending) return <PageContainer><Skeleton className="h-64 w-full" /></PageContainer>;
  if (error) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const rows = pendingAtStage(all, stage);
  // The SLA banner counts the whole queue, not the filtered view: a search must not
  // make an overdue review stop existing.
  const overdue = rows.filter((a) => a.managerOverdue).length;
  const q = query.trim().toLowerCase();
  const data = q
    ? rows.filter((a) => `${a.employeeName} ${a.quarterLabel}`.toLowerCase().includes(q))
    : rows;

  return (
    <PageContainer>
      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center sm:py-16">
            <CheckCircle2 className="mx-auto mb-3 size-7 text-success" />
            <p className="font-medium">Nothing waiting on you at {STAGE_LABELS[stage]}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {/* Names the other queue rather than implying nothing exists anywhere:
                  somebody holding two stages should not read an empty HR queue as an
                  empty company. */}
              {all.length > rows.length
                ? `${all.length - rows.length} review(s) are waiting on you at another stage.`
                : 'Reviews appear here as soon as the previous stage signs one off.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {overdue > 0 && (
            <Alert variant="destructive">
              <Clock className="size-4" />
              <AlertDescription>
                {overdue} review{overdue === 1 ? ' is' : 's are'} past the 3-day deadline.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 py-4 sm:py-4">
              <SearchForm
                className="min-w-[220px] flex-1"
                placeholder="Search name or quarter"
                onSearch={setQuery}
              />
              <p className="text-sm text-muted-foreground">
                {data.length} of {rows.length} shown
              </p>
            </CardContent>
          </Card>

          {data.length === 0 ? (
            <EmptyState art={LostArt} title={`No review matches “${query.trim()}”`} />
          ) : (
          <div className="space-y-3">
            {data.map((a) => {
              const due = dueLabel(a.managerDueAt, a.managerOverdue);
              const rated = recordedRating(a);
              return (
                <Card key={a.id}>
                  <CardContent className="flex flex-wrap items-center gap-4 py-4 sm:py-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <p className="font-medium">{a.employeeName}</p>
                        {rated?.overallRating != null && (
                          <span className="pms-num text-sm font-semibold">
                            {rated.overallRating} · {toGrade(rated.overallRating)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {a.quarterLabel} ·{' '}
                        {a.submittedAt
                          ? `submitted ${new Date(a.submittedAt).toLocaleDateString()}`
                          : 'not yet submitted'}
                        {rated?.overallRating != null
                          ? ` · rated at ${STAGE_LABELS[rated.stage]}`
                          : ' · not rated yet'}
                      </p>
                    </div>

                    <Badge variant={assessmentStatusVariant(a.status)} className={PILL_CLASS}>
                      {a.stageAwaiting ? STAGE_LABELS[a.stageAwaiting] : 'Complete'}
                    </Badge>

                    <Badge variant={slaVariant(due.state)} className={cn(PILL_CLASS, 'tabular-nums')}>
                      {due.text}
                    </Badge>

                    <Button hint={"Review this assessment"} asChild variant="outline">
                      <Link to={`/team/${a.employeeId}/assessment?quarter=${a.quarterId}`}>
                        Review
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
